import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
} from 'react-native';
import { COLORS, FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

// ============================================================
// Types
// ============================================================

export interface DropdownOption {
  value: string | number;
  label: string;
}

export interface SelectDropdownProps {
  options: DropdownOption[];
  value: string | number | null | undefined;
  onChange: (value: string | number) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  searchable?: boolean;
  maxHeight?: number;
  zIndex?: number;
}

// ============================================================
// Native Select Component - No z-index issues!
// ============================================================

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  required = false,
  disabled = false,
  error,
}) => {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();

  // Use provided placeholder or default to translated value
  const displayPlaceholder = placeholder ?? t('common.select');

  // Theme colors
  const themeColors = {
    background: colors.inputBackground,
    backgroundDisabled: isDark ? colors.backgroundAlt : '#F1F5F9',
    border: colors.border,
    textPrimary: colors.textPrimary,
    textPlaceholder: colors.textMuted,
    labelText: colors.textPrimary,
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    // Convert back to number if the original option value was a number
    const option = options.find(opt => String(opt.value) === newValue);
    if (option) {
      onChange(option.value);
    } else {
      onChange(newValue);
    }
  };

  return (
    <View style={styles.container}>
      {/* Label */}
      {label && (
        <View style={styles.labelRow}>
          {required && <Text style={styles.requiredStar}>*</Text>}
          <Text style={[styles.label, { color: themeColors.labelText }]}>{label}</Text>
        </View>
      )}

      {/* Native Select Container */}
      <View style={styles.selectContainer}>
        {Platform.OS === 'web' ? (
          // Web: Use native HTML select
          <select
            value={value !== null && value !== undefined ? String(value) : ''}
            onChange={handleChange}
            disabled={disabled}
            dir="rtl"
            style={{
              width: '100%',
              height: 44,
              padding: '10px 40px 10px 12px',
              border: error ? '1px solid #EF4444' : `1px solid ${themeColors.border}`,
              borderRadius: 8,
              backgroundColor: disabled ? themeColors.backgroundDisabled : themeColors.background,
              color: value ? themeColors.textPrimary : themeColors.textPlaceholder,
              fontSize: 14,
              fontFamily: FONTS.regular,
              textAlign: 'right',
              direction: 'rtl',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              outline: 'none',
            }}
            onFocus={(e) => {
              if (!error) {
                e.target.style.borderColor = COLORS.primary;
                e.target.style.borderWidth = '2px';
              }
            }}
            onBlur={(e) => {
              e.target.style.borderColor = error ? '#EF4444' : themeColors.border;
              e.target.style.borderWidth = '1px';
            }}
          >
            <option value="" disabled style={{ color: themeColors.textPlaceholder }}>
              {displayPlaceholder}
            </option>
            {options.map((option) => (
              <option
                key={String(option.value)}
                value={String(option.value)}
                style={{ color: themeColors.textPrimary }}
              >
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          // Native: Fallback text (use Picker from @react-native-picker/picker for native)
          <Text style={[styles.nativeFallback, { backgroundColor: themeColors.background, borderColor: themeColors.border, color: themeColors.textPrimary }]}>
            {options.find(opt => String(opt.value) === String(value))?.label || displayPlaceholder}
          </Text>
        )}

        {/* Arrow Icon handled by CSS background-image */}
      </View>

      {/* Error Message */}
      {error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
};

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  // Label
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  requiredStar: {
    fontSize: 14,
    color: '#EF4444',
  },
  // Select Container
  selectContainer: {
    position: 'relative',
    width: '100%',
  },
  // Error
  errorText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
    marginTop: 4,
  },
  // Native Fallback
  nativeFallback: {
    height: 44,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
  },
});

export default SelectDropdown;
